import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

interface CacheEntry {
  data: any;
  timestamp: number;
  expiresIn: number; // milliseconds
}

interface CacheContextType {
  getCache: (key: string) => any | null;
  setCache: (key: string, data: any, expiresIn?: number) => void;
  clearCache: (key?: string) => void;
  invalidateCache: (pattern: string) => void;
}

const CacheContext = createContext<CacheContextType | undefined>(undefined);

export const useCache = () => {
  const context = useContext(CacheContext);
  if (!context) {
    throw new Error('useCache must be used within a CacheProvider');
  }
  return context;
};

interface CacheProviderProps {
  children: React.ReactNode;
}

export const CacheProvider: React.FC<CacheProviderProps> = ({ children }) => {
  const [cache, setCacheState] = useState<Record<string, CacheEntry>>({});

  // Get data from cache if not expired
  const getCache = useCallback((key: string): any | null => {
    const entry = cache[key];
    if (!entry) return null;

    const now = Date.now();
    if (now - entry.timestamp > entry.expiresIn) {
      // Cache expired, remove it
      setCacheState(prev => {
        const newCache = { ...prev };
        delete newCache[key];
        return newCache;
      });
      return null;
    }

    return entry.data;
  }, [cache]);

  // Set data in cache with expiration time
  const setCache = useCallback((key: string, data: any, expiresIn: number = 60000) => {
    setCacheState(prev => ({
      ...prev,
      [key]: {
        data,
        timestamp: Date.now(),
        expiresIn,
      },
    }));
  }, []);

  // Clear specific cache or all cache
  const clearCache = useCallback((key?: string) => {
    if (key) {
      setCacheState(prev => {
        const newCache = { ...prev };
        delete newCache[key];
        return newCache;
      });
    } else {
      setCacheState({});
    }
  }, []);

  // Invalidate cache entries matching a pattern
  const invalidateCache = useCallback((pattern: string) => {
    setCacheState(prev => {
      const newCache = { ...prev };
      Object.keys(newCache).forEach(key => {
        if (key.includes(pattern)) {
          delete newCache[key];
        }
      });
      return newCache;
    });
  }, []);

  // Cleanup expired cache entries every minute
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      setCacheState(prev => {
        const newCache = { ...prev };
        let hasExpired = false;

        Object.entries(newCache).forEach(([key, entry]) => {
          if (now - entry.timestamp > entry.expiresIn) {
            delete newCache[key];
            hasExpired = true;
          }
        });

        return hasExpired ? newCache : prev;
      });
    }, 60000); // Check every minute

    return () => clearInterval(interval);
  }, []);

  return (
    <CacheContext.Provider value={{ getCache, setCache, clearCache, invalidateCache }}>
      {children}
    </CacheContext.Provider>
  );
};
